import React, { useEffect, useState, useRef } from 'react';
import { Flex, Text, Box } from 'rebass';
import { useParams } from 'react-router-dom';
import { useImmer } from 'use-immer';
import ReactPlayer from 'react-player';
import store from 'store';
import VideoPreviewTime from './VideoPreviewTime';
import { useFrameNow } from './hooks/timer';
import TrimSlider from './TrimSlider';
// import SubmitButton from '../app/formik/SubmitButton';
import AsyncButton from './AsyncButton';
import Grid from 'app/grid/index';
import CirclePlayButton from 'chats/CirclePlayButton';
import useEntity from 'entities/hooks/use-entity';
import BufferingOverlay from 'app/modal/BufferingOverlay';
import { patch } from 'utils/api';

function VideoTrimmer({ onClose, urlToPlay, isFromMobile, video, monthId, clipDetails, onSubmit, isForEdit }) {
  const [isBuffering, setIsBuffering] = useState(true);
  const [duration, setDuration] = useState(undefined);
  const [isPlaying, setIsPlaying] = useState(false);
  const frameNow = useFrameNow(isPlaying);
  const { entity: videoDetails } = useEntity(clipDetails?.video);

  const params = useParams();
  const { entity } = useEntity(params);
  const firstBookId = entity?.books?.[0]?.id || '';

  const clip = clipDetails
  const [value, updateValue] = useImmer([
    clip?.['trim-start'] || 0,
    clip?.['trim-start'] || 0,
    clip?.['trim-end'] || 10,
  ]);

  const totalTime = (video || videoDetails)?.['duration-in-seconds'];
  const currentOffsetTime = value[1] || 0;

  const videoRef = useRef(null);
  const setCurrentTime = (time) => {
    updateValue((draft) => {
      draft.splice(1, 1, time);
    });
  };

  useEffect(() => {
    if (isPlaying && frameNow && videoRef.current) {
      if (videoRef.current.getCurrentTime() >= value[2]) {
        setIsPlaying(false);
      } else {
        setCurrentTime(videoRef.current.getCurrentTime());
      }
    }
  }, [frameNow]);

  useEffect(() => {
    if (isPlaying) {
      videoRef.current?.props.onPlay && videoRef.current.props.onPlay(); // Ensure the function is called only if it exists
    } else {
      videoRef.current?.props.onPause && videoRef.current.props.onPause(); // Ensure the function is called only if it exists
    }
  }, [isPlaying]);

  const handleSliderChange = (nextValue) => {
    let [startTime, currentTime] = nextValue; // Get the start time from the slider
    let endTime = startTime + 10; // Ensure the end time is always 10 seconds after the start time
    if (endTime > duration) {
      endTime = duration; // Set the end time to the video duration
      startTime = Math.max(0, duration - 10); // Adjust the start time to maintain a 10-second difference
    }
    if (currentTime > startTime && currentTime < endTime) {
      currentTime = currentTime
    }
    else {
      currentTime = startTime
    }

    updateValue((draft) => {
      draft[0] = startTime; // Update the start time
      draft[1] = currentTime; // Update the current time to match the start time
      draft[2] = endTime; // Update the end time to be 10 seconds after the start time
    });

    videoRef.current.seekTo(currentTime, 'seconds');
  };

  const isDirty = clip?.['trim-start'] !== value[0] || clip?.['trim-end'] !== value[2];

  return (
    <Box >
      <BufferingOverlay isBuffering={isBuffering} m="auto">
        <Box className="player-wrapper" width={"300px"} height="300px" sx={{ borderRadius: '11.559px', overflow: 'hidden' }}>
          <ReactPlayer
            ref={videoRef}
            onBuffer={() => setIsBuffering(true)}
            onBufferEnd={() => setIsBuffering(false)}
            width="100%"
            height="100%"
            onReady={() => {
              setIsBuffering(false);
            }}
            playing={isPlaying}
            playsinline
            stopOnUnmount={false}
            // onPlay={onPlay}
            volume={1}
            // onBuffer={onPause}
            progressInterval={0.5}
            url={video?.['asset-url'] || urlToPlay}
            style={{
              height: "inherit",
              width: 'inherit',
              objectFit: 'contain',
              objectPosition: 'center',
            }}
            onError={(e) => {
              store.set('sentryError', `${e} /VideoTrimmer Component React-player error`);
            }}
            onDuration={(e) => {
              videoRef.current.seekTo(e, 'seconds');
              setDuration(e);
            }}
          />
        </Box>
      </BufferingOverlay>
      {/* {duration && ( */}
      <Box width={400} maxWidth="90vw" sx={{ borderRadius: 'medium' }} mb={isFromMobile ? 0 : 2} p={2}>
        <Flex mt={1} justifyContent="center" alignItems="center">
          <VideoPreviewTime seconds={currentOffsetTime} />
          <Text color="#64748B" px={1} fontSize={0}>
            /
          </Text>
          <VideoPreviewTime seconds={duration || totalTime} />
        </Flex>
        <Grid gridGap={4} gridTemplateColumns="max-content 1fr">
          <Flex justifyContent="center">
            <CirclePlayButton
              isPlaying={isPlaying}
              onClick={async () => {
                if (videoRef.current.getCurrentTime() >= value[2]) {
                  videoRef.current.seekTo(value[0], 'seconds');
                  setCurrentTime(value[0]);
                }
                setIsPlaying((state) => !state);
              }}
            />
          </Flex>
          <Box pt={2}>
            <TrimSlider
              value={[value[0], value[1], value[2]]} // Only pass the start time to the slider
              min={0}
              max={duration} // Ensure the slider stops at the last valid start time
              step={0.016}
              onChange={handleSliderChange}
              onClick={() => setIsPlaying(false)}
            />
          </Box>
        </Grid>
        {/* <Grid alignItems="baseline" gridGap={4} gridTemplateColumns="max-content 1fr" mt={3}> */}
        <Flex mt={3} >
          <AsyncButton
            disabled={!isDirty}
            onClick={async () => {

              await patch(`/v3/books/${firstBookId}/watch_me_grow_months/${monthId}/watch_me_grow_clips/${clipDetails?.id}`, {
                data: {
                  attributes: {
                    'trim-start': value[0] || null, 'trim-end': value[2] || null,
                  }
                }
              });

              if (isForEdit)
                onClose()
              else
                onSubmit()
            }}
            m="auto"
          >
            Save
          </AsyncButton>
        </Flex>
        {/* </Grid> */}
      </Box>
      {/* )} */}
    </Box>
  );
}

export default VideoTrimmer;
